import React, { useEffect, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { endOfDay, format, startOfDay, startOfMonth, startOfWeek, startOfYear, subDays } from 'date-fns';

import { UseOutletContextPos } from './Pos';
import { companyYearList } from '../../../scripts/list.filter.script';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { PosCashierDA } from '../../../services/admin/pos.cashier.service';
import { PosSaleDA } from '../../../services/admin/pos.sale.service';
import { PosCashier } from '../../../models/pos.model';
import { CompanyPosSales, PosSale, PosSalePayment } from '../../../models/pos.sale.model';
import { TreasuryAccount } from '../../../models/treasury.account.model';
import { Error } from '../../../models/error.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { getError } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { formatContact, formatMoney } from '../../../scripts/format.value.script';
import { sortColumn } from '../../../scripts/table.script';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { paymentMethodValue } from '../../../libraries/payment.library';
import { usePaginationUrl } from '../../../hooks/usePaginationUrl';

import AppPreloader from '../../../components/Preloader';
import AppPagination from '../../../components/element/Pagination';
import AppSortTable from '../../../components/element/SortTable';
import AppStorageRemove from '../../../components/element/StorageRemove';

export interface PosViewPageProps {};

let errorResponse: Error, posCashierResponse: PosCashier, companyPosSalesResponse: CompanyPosSales;

const PosViewPage: React.FunctionComponent<PosViewPageProps> = props => {
  const {setRoute, companyForUser, treasuryAccountsForCompany} = UseOutletContextPos()
  const {lang} = StateLanguage()
  const param = useParams()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [button, setButton] = useState(false)
  const [filter, setFilter] = useState({interval: '', start_date: '', end_date: '', sort_field: '', sort_mode: ''})
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [posCashier, setPosCashier] = useState<PosCashier | null>(null)
  const [posSales, setPosSales] = useState<PosSale[] | null>(null)
  const [storage, setStorage] = useState(null as any)

  const loadPosCashier = async (id_company: string, id_pos_cashier: string) => {
    await PosCashierDA.getCashier(id_company, id_pos_cashier).then( (response) => {
      if (response.status === 200) {
        posCashierResponse = response.data
        setPosCashier(posCashierResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadPosSales = async (id_company: string, id_pos_cashier: string, page: number) => {
    await PosSaleDA.getSales(id_company, id_pos_cashier, filter.interval, filter.start_date, filter.end_date, filter.sort_field, filter.sort_mode, page, pagination.limit).then( (response) => {
      if (response.status === 200) {
        companyPosSalesResponse = response.data
        const salesFilter = companyPosSalesResponse.filter ?? { interval: filter.interval, start_date: filter.start_date, end_date: filter.end_date, sort_field: filter.sort_field, sort_mode: filter.sort_mode }

        localStorage.setItem('filter_pos_sales', JSON.stringify({
          company_id: companyPosSalesResponse.id_company ?? companyForUser?.id ?? '',
          pos_cashier_id: companyPosSalesResponse.id_pos_cashier ?? param.id ?? '',
          interval: salesFilter.interval,
          start_date: salesFilter.start_date,
          end_date: salesFilter.end_date,
          sort_field: salesFilter.sort_field,
          sort_mode: salesFilter.sort_mode
        }))

        const totalPages = companyPosSalesResponse.pagination?.pages || 1

        if (page > totalPages && totalPages >= 1) {
          setUrlPage(totalPages)
          loadPosSales(id_company, id_pos_cashier, totalPages)
          return
        }

        let pagesOfPagination: number[] = []
        for (let i = 1; i <= totalPages; i++) {
          pagesOfPagination.push(i)
        }

        setPosSales(companyPosSalesResponse.sales ?? companyPosSalesResponse.items ?? [])
        setPagination({...pagination, current: companyPosSalesResponse.pagination?.page || 1, total: companyPosSalesResponse.pagination?.total || 0, pages: pagesOfPagination})
        setUrlPage(companyPosSalesResponse.pagination?.page || 1)
        setButton(Boolean(salesFilter.interval || salesFilter.start_date || salesFilter.end_date))
        setStorage((localStorage.getItem('filter_pos_sales')) ? JSON.parse(localStorage.getItem('filter_pos_sales')!) : null)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleChangeInterval = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    const now: Date = new Date()
    const df = `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`

    switch (event.target.value) {
      case 'today':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfDay(now), df), end_date: format(endOfDay(now), df)})
        break;
      case 'yesterday':
        const yesterday = subDays(now, 1)
        setFilter({...filter, interval: event.target.value, start_date: format(startOfDay(yesterday), df), end_date: format(endOfDay(yesterday), df)})
        break;
      case 'week':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfWeek(now), df), end_date: format(endOfDay(now), df)})
        break;
      case 'month':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfMonth(now), df), end_date: format(endOfDay(now), df)})
        break;
      case 'year':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfYear(now), df), end_date: format(endOfDay(now), df)})
        break;
      default:
        setFilter({...filter, interval: event.target.value, start_date: format((new Date(`${event.target.value}-01-01T00:00:00.000`)), df), end_date: format((new Date(`${event.target.value}-12-31T23:59:59.999`)), df)})
        break;
    }
  }

  const handleChangeStartDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''
    setFilter({...filter, interval: '', start_date: date})
  }

  const handleChangeEndDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T23:59:59.999`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''
    setFilter({...filter, interval: '', end_date: date})
  }

  const loadPagination = (current: number) => {
    if (!companyForUser || !posCashier) return null
    setUrlPage(current)
    loadPosSales(companyForUser.id, posCashier.id, current)
  }

  const executeSortList = (item: string) => {
    if (companyForUser && posCashier) {
      sortColumn(item, filter)
      setUrlPage(1)
      loadPosSales(companyForUser.id, posCashier.id, 1)
    }
  }

  const executeFilterRemove = (item: string) => {
    if (companyForUser && posCashier && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0)) {
      for (let key of Object.keys(filter) as Array<keyof typeof filter>) {
        if (key === item) {
          filter[key] = ''
        }
        if (item === "start_date") {
          filter.interval = ''
        }
        if (item === "end_date") {
          filter.interval = ''
        }
        if (item === "interval") {
          filter.start_date = ''
          filter.end_date = ''
        }
      }
      setUrlPage(1)
      loadPosSales(companyForUser.id, posCashier.id, 1)
    }
  }

  const executeFilterReset = () => {
    if (companyForUser && posCashier && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0)) {
      filter.interval = ''
      filter.start_date = ''
      filter.end_date= ''
      filter.sort_field = ''
      filter.sort_mode = ''
      setUrlPage(1)
      loadPosSales(companyForUser.id, posCashier.id, 1)
    }
  }

  const executeFilterApply = () => {
    if (companyForUser && posCashier && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0)) {
      filter.sort_field = ''
      filter.sort_mode = ''
      setUrlPage(1)
      loadPosSales(companyForUser.id, posCashier.id, 1)
    }
  }

  function paumentMethods(payments: PosSalePayment[], treasuryAccounts: TreasuryAccount[]): string {
    return payments.map((item) => {
      const source = item.source === "credit-note" ? lang.labels.creditNote : treasuryAccounts.find((sub_item) => sub_item.id === (item.source || item.id_treasury_account))?.name
      const currencyTag = item.currency_code && item.currency_code !== 'CHF' ? ` [${item.currency_code}]` : ''
      return `${paymentMethodValue(lang, item.method)} (${source})${currencyTag}`
    }).join(', ')
  }

  const companyId = companyForUser?.id ?? ''
  const posId = param.id ?? ''

  useEffect( () => {
    if (!companyId || !posId) return

    let filter_pos_sales: any = (localStorage.getItem('filter_pos_sales')) ? JSON.parse(localStorage.getItem('filter_pos_sales')!) : null

    filter.interval = (filter_pos_sales && filter_pos_sales.company_id === companyId) ? filter_pos_sales.interval : ''
    filter.start_date = (filter_pos_sales && filter_pos_sales.company_id === companyId) ? filter_pos_sales.start_date : ''
    filter.end_date = (filter_pos_sales && filter_pos_sales.company_id === companyId) ? filter_pos_sales.end_date : ''
    filter.sort_field = (filter_pos_sales && filter_pos_sales.company_id === companyId) ? filter_pos_sales.sort_field : ''
    filter.sort_mode = (filter_pos_sales && filter_pos_sales.company_id === companyId) ? filter_pos_sales.sort_mode : ''

    setPosCashier(null)
    setPosSales(null)
    loadPosCashier(companyId, posId)
    loadPosSales(companyId, posId, urlPage)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, posId])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.pos, branch: lang.labels.viewPOS}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && treasuryAccountsForCompany && posCashier && posSales
        ?
        <>
          <div className="card mb-5">
            <div className="card-header">
              <div className="card-title">
                <h2>{posCashier.name}</h2>
                { posCashier.stock_enabled === false &&
                  <span className="badge badge-light-secondary ms-3 fs-8">{lang.labels.withoutStockManagement}</span>
                }
              </div>
              <div className="card-toolbar">
                <Link to={"/main/pos/sale/create/" + (posCashier.id)} className="btn btn-primary">
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                        <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                        <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.createSale}
                  </div>
                </Link>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-6 col-xl-3">
                  <div className="notice bg-light-primary rounded p-3">
                    <div className="fw-bold text-muted mb-2">{lang.labels.code}</div>
                    <div className="fw-bolder fs-4">{posCashier.code}</div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-3">
                  <div className="notice bg-light-dark rounded p-3">
                    <div className="fw-bold text-muted mb-2">{lang.labels.defaultCustomerSale}</div>
                    <div className="fw-bolder fs-4">{(() => { const c = (posCashier as any).default_customer ?? posCashier.customer_default; if (!c) return '-'; if (typeof c === 'object') return c.name ?? formatContact(c); return '-' })()}</div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-3">
                  <div className="notice bg-light-info rounded p-3">
                    <div className="fw-bold text-muted mb-2">{lang.labels.totalTransactions}</div>
                    <div className="fw-bolder fs-4">{pagination.total}</div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-3">
                  <div className="notice bg-light-success rounded p-3">
                    <div className="fw-bold text-muted mb-2">{lang.labels.totalAmount}</div>
                    <div className="fw-bolder fs-4">{companyForUser.currency?.code} {formatMoney(posSales.reduce((sum, sale) => sum + sale.amount.total, 0))}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card card-flush">
            <div className="card-header">
              <div className="card-title">
                <div className="badge badge-lg badge-light-primary">
                  <div className="d-flex align-items-center flex-wrap">
                    <span className="svg-icon svg-icon-2 svg-icon-primary me-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path opacity="0.3" d="M14.1 15.013C14.6 16.313 14.5 17.813 13.7 19.113C12.3 21.513 9.29999 22.313 6.89999 20.913C5.29999 20.013 4.39999 18.313 4.39999 16.613C5.09999 17.013 5.99999 17.313 6.89999 17.313C8.39999 17.313 9.69998 16.613 10.7 15.613C11.1 15.713 11.5 15.813 11.9 15.813C12.7 15.813 13.5 15.513 14.1 15.013ZM8.5 12.913C8.5 12.713 8.39999 12.513 8.39999 12.313C8.39999 11.213 8.89998 10.213 9.69998 9.613C9.19998 8.313 9.30001 6.813 10.1 5.513C10.6 4.713 11.2 4.11299 11.9 3.71299C10.4 2.81299 8.49999 2.71299 6.89999 3.71299C4.49999 5.11299 3.70001 8.113 5.10001 10.513C5.80001 11.813 7.1 12.613 8.5 12.913ZM16.9 7.313C15.4 7.313 14.1 8.013 13.1 9.013C14.3 9.413 15.1 10.513 15.3 11.713C16.7 12.013 17.9 12.813 18.7 14.113C19.2 14.913 19.3 15.713 19.3 16.613C20.8 15.713 21.8 14.113 21.8 12.313C21.9 9.513 19.7 7.313 16.9 7.313Z" fill="black" />
                        <path d="M9.69998 9.61307C9.19998 8.31307 9.30001 6.81306 10.1 5.51306C11.5 3.11306 14.5 2.31306 16.9 3.71306C18.5 4.61306 19.4 6.31306 19.4 8.01306C18.7 7.61306 17.8 7.31306 16.9 7.31306C15.4 7.31306 14.1 8.01306 13.1 9.01306C12.7 8.91306 12.3 8.81306 11.9 8.81306C11.1 8.81306 10.3 9.11307 9.69998 9.61307ZM8.5 12.9131C7.1 12.6131 5.90001 11.8131 5.10001 10.5131C4.60001 9.71306 4.5 8.91306 4.5 8.01306C3 8.91306 2 10.5131 2 12.3131C2 15.1131 4.2 17.3131 7 17.3131C8.5 17.3131 9.79999 16.6131 10.8 15.6131C9.49999 15.1131 8.7 14.1131 8.5 12.9131ZM18.7 14.1131C17.9 12.8131 16.7 12.0131 15.3 11.7131C15.3 11.9131 15.4 12.1131 15.4 12.3131C15.4 13.4131 14.9 14.4131 14.1 15.0131C14.6 16.3131 14.5 17.8131 13.7 19.1131C13.2 19.9131 12.6 20.5131 11.9 20.9131C13.4 21.8131 15.3 21.9131 16.9 20.9131C19.3 19.6131 20.1 16.5131 18.7 14.1131Z" fill="black" />
                      </svg>
                    </span>
                    <span className="text-capitalize">{lang.labels.cashier}</span>
                    <span className="svg-icon svg-icon-2 svg-icon-primary mx-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12.6343 12.5657L8.45001 16.75C8.0358 17.1642 8.0358 17.8358 8.45001 18.25C8.86423 18.6642 9.5358 18.6642 9.95001 18.25L15.4929 12.7071C15.8834 12.3166 15.8834 11.6834 15.4929 11.2929L9.95001 5.75C9.5358 5.33579 8.86423 5.33579 8.45001 5.75C8.0358 6.16421 8.0358 6.83579 8.45001 7.25L12.6343 11.4343C12.9467 11.7467 12.9467 12.2533 12.6343 12.5657Z" fill="black" />
                      </svg>
                    </span>
                    <span>{lang.labels.listOfSales}</span>
                  </div>
                </div>
              </div>
              <div className="card-toolbar">
                <Dropdown>
                  <Dropdown.Toggle variant={`btn ${button ? "btn-primary" : "btn-light-primary"}`}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M19.0759 3H4.72777C3.95892 3 3.47768 3.83148 3.86067 4.49814L8.56967 12.6949C9.17923 13.7559 9.5 14.9582 9.5 16.1819V19.5072C9.5 20.2189 10.2223 20.7028 10.8805 20.432L13.8805 19.1977C14.2553 19.0435 14.5 18.6783 14.5 18.273V13.8372C14.5 12.8089 14.8171 11.8056 15.408 10.964L19.8943 4.57465C20.3596 3.912 19.8856 3 19.0759 3Z" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.filter}
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-300px w-md-325px" align="end">
                    <div className="mt-5 mx-5">
                      <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
                    </div>
                    <div className="separator border-gray-200"></div>
                    <div className="mt-3 m-5">
                      <div className="row mb-2">
                        <label className="col-5 col-form-label-sm fw-bold fs-7">{lang.labels.timeInterval}:</label>
                        <div className="col-7 align-content-center">
                          <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="interval" value={filter.interval} onChange={handleChangeInterval}>
                            <option value="" className="d-none">{lang.labels.select}</option>
                            <option value="today">{lang.labels.today}</option>
                            <option value="yesterday">{lang.labels.yesterday}</option>
                            <option value="week">{lang.labels.thisWeek}</option>
                            <option value="month">{lang.labels.thisMonth}</option>
                            <option value="year">{lang.labels.thisYear}</option>
                            { companyForUser?.created_at && companyYearList(companyForUser.created_at).filter((item) => (item !== new Date().getFullYear())).map (( (item, index) => { return (
                              <option key={index} value={item}>{lang.labels.year} {item}</option>
                            )}))}
                          </Form.Select>
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-6 mb-2 mb-md-0">
                          <label className="form-label fs-7">{lang.labels.startDate}:</label>
                          <input className="form-control form-control-sm" type="date" name="start-date" max={filter.end_date?.substring(0, 10) ?? ''} value={filter.start_date?.substring(0, 10) ?? ''} onChange={handleChangeStartDate} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fs-7">{lang.labels.endDate}:</label>
                          <input className="form-control form-control-sm" type="date" name="end-date" min={filter.start_date?.substring(0, 10) ?? ''} value={filter.end_date?.substring(0, 10) ?? ''} onChange={handleChangeEndDate} />
                        </div>
                      </div>
                      <div className="d-flex justify-content-end mt-5">
                        <Dropdown.Item bsPrefix="btn btn-sm btn-light btn-active-light-primary" onClick={executeFilterReset}>{lang.labels.reset}</Dropdown.Item>
                        <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={executeFilterApply}>{lang.labels.apply}</Dropdown.Item>
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            { storage && button &&
              <div className="card-scroll d-flex bg-light-primary rounded border-0 p-3 mx-7 mb-4">
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.interval.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("interval")}>
                  <AppStorageRemove attribute={lang.labels.interval} description={storage.interval}></AppStorageRemove>
                </button>
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.start_date.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("start_date")}>
                  <AppStorageRemove attribute={lang.labels.start} description={dateFormat(lang.code, storage.start_date)}></AppStorageRemove>
                </button>
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.end_date.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("end_date")}>
                  <AppStorageRemove attribute={lang.labels.end} description={dateFormat(lang.code, storage.end_date)}></AppStorageRemove>
                </button>
              </div>
            }
            <div className="card-body pt-0">
              <div className="dataTables_wrapper">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed mb-2">
                    <thead className="border-bottom">
                      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                        <th className="w-100px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("created date")}>
                            <span className="me-1">{lang.labels.date}</span>
                            <AppSortTable attribute={"created date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="w-150px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("sale number")}>
                            <span className="me-1">{lang.labels.saleNumber}</span>
                            <AppSortTable attribute={"sale number"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-150px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("customer")}>
                            <span className="me-1">{lang.labels.customer}</span>
                            <AppSortTable attribute={"customer"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-200px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("payment methods")}>
                            <span className="me-1">{lang.labels.paymentMethods}</span>
                            <AppSortTable attribute={"payment methods"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-100px text-end">
                          <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount total")}>
                            <span className="me-1">{lang.labels.amountTotal}</span>
                            <AppSortTable attribute={"amount total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    { pagination.total > 0
                      ?
                      <tbody className="text-secondary fw-bold">
                        { posSales.map (( (item, index) => { return (
                          <tr key={index} className="row-dashed">
                            <td className="text-nowrap">{dateFormat(lang.code, item.created_at)}</td>
                            <td>
                              <Link to={"/main/pos/sale/view/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{item.sale_number}</Link>
                            </td>
                            <td>{formatContact(item.customer)}</td>
                            <td>{paumentMethods(item.payments, treasuryAccountsForCompany)}</td>
                            <td className="text-end text-nowrap">{item.currency.code} {formatMoney(item.amount.total)}</td>
                          </tr>
                        )}))}
                      </tbody>
                      :
                      <tbody>
                        <tr className="text-secondary fw-bold">
                          <td colSpan={5} className="text-center">{lang.labels.noRecordsFound}</td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
                <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>
              </div>
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default PosViewPage;
